import { useEffect, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { capi, errMsg } from "./chatApi";

const inp = "w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30";

export const TaskDialog = ({ m, people, me, onClose, onDone }) => {
  const [v, setV] = useState({ title: (m?.message || m?.file_name || "").slice(0, 80), assigned_to: "", due_date: "", priority: "medium", description: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await capi("post", `/chat/messages/${m.id}/task`, v); toast.success("Task created and linked to the message"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!m} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="chat-task-dialog">
        <DialogHeader><DialogTitle>Turn message into a task</DialogTitle><DialogDescription>The task links back here and the chat shows its live status.</DialogDescription></DialogHeader>
        <input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Task title" className={inp} data-testid="chat-task-title" />
        <textarea value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} rows={2} placeholder="Details (optional)" className={inp} data-testid="chat-task-desc" />
        <div className="grid grid-cols-2 gap-2">
          <select value={v.assigned_to} onChange={(e) => setV({ ...v, assigned_to: e.target.value })} className={inp} data-testid="chat-task-assignee">
            <option value="">Assign to…</option><option value={me.id}>Me</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.team}</option>)}</select>
          <input type="date" value={v.due_date} onChange={(e) => setV({ ...v, due_date: e.target.value })} className={inp} data-testid="chat-task-due" />
        </div>
        <div className="flex gap-1">{["low", "medium", "high"].map((p) => (
          <button key={p} onClick={() => setV({ ...v, priority: p })} className={`flex-1 rounded-lg border py-1.5 text-xs font-semibold capitalize ${v.priority === p ? "border-indigo-600 bg-indigo-600 text-white" : ""}`} data-testid={`chat-task-priority-${p}`}>{p}</button>))}</div>
        <button onClick={save} disabled={busy || !v.title.trim()} className="w-full rounded-lg bg-indigo-600 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="chat-task-save">{busy ? "Creating…" : "Create task"}</button>
      </DialogContent>
    </Dialog>
  );
};

const KINDS = [["parcel_query", "Parcel query"], ["partner_query", "Partner query"], ["task", "Task"]];

export const LinkDialog = ({ m, onClose, onDone }) => {
  const [kind, setKind] = useState("parcel_query");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!m) return undefined;
    setRows(null);
    const t = setTimeout(() => capi("get", `/chat/link-search?kind=${kind}&q=${encodeURIComponent(q)}`).then(setRows).catch((e) => toast.error(errMsg(e))), 250);
    return () => clearTimeout(t);
  }, [kind, q, m]);
  const pick = async (r) => {
    try { await capi("post", `/chat/messages/${m.id}/link`, { kind, id: r.id }); toast.success("Linked — status shows on the message"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!m} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="chat-link-dialog">
        <DialogHeader><DialogTitle>Link message</DialogTitle><DialogDescription>Only a link is added — the query's own conversation is not changed.</DialogDescription></DialogHeader>
        <div className="flex gap-1">{KINDS.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} className={`flex-1 rounded-lg border py-1.5 text-xs font-semibold ${kind === k ? "border-indigo-600 bg-indigo-600 text-white" : ""}`} data-testid={`chat-link-kind-${k}`}>{l}</button>))}</div>
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, code, tracking, title…" className={`${inp} pl-8`} data-testid="chat-link-search" /></div>
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {!rows && <Loader2 className="mx-auto my-4 h-4 w-4 animate-spin text-slate-400" />}
          {rows?.map((r) => (
            <button key={r.id} onClick={() => pick(r)} className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm hover:border-indigo-300 hover:bg-indigo-50" data-testid={`chat-link-result-${r.id}`}>
              <span className="min-w-0 flex-1"><span className="block truncate font-medium">{r.title}</span><span className="text-xs text-slate-400">{r.sub}</span></span>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold capitalize">{String(r.status || "").replace("_", " ")}</span></button>))}
          {rows && !rows.length && <p className="py-4 text-center text-sm text-slate-400">Nothing found.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
